import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";

import { ManagedUserGroup } from "types/generated";
import NotFoundPage from "components/error/NotFoundPage";
import useTitle from "hooks/useTitle";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";
import FlashContext from "components/FlashMessages/FlashContext";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";

import { showIdpGroupMappingDetailsDrawer } from "./DetailsDrawer";
import { GET_MANAGED_USER_GROUPS } from "./gql";
import IdpGroupMappingEmpty from "./Empty";
import IdpGroupMappingPageLayout from "./PageLayout";
import { SettingsContext } from "../Context";
import IdpGroupTable from "./Table";
import { INITIAL_SORT_DIRECTION, INITIAL_SORT_OPTION } from "./constants";

type GetManagedUserGroupsGql = {
  managedUserGroups: ManagedUserGroup[];
};

const IdpGroupMapping = () => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { accountName } = useTypedContext(AccountContext);
  const { onError } = useTypedContext(FlashContext);

  // Initialize default sort parameters in URL
  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const { loading, data, error } = useQuery<GetManagedUserGroupsGql>(GET_MANAGED_USER_GROUPS, {
    onError,
  });

  useTitle(`Organization Settings · IdP group mapping · ${accountName}`);

  /**
   * This effect is used to open the details drawer
   * when the user clicks on a group in the users details drawer.
   * For more details check here:
   * src/views/Account/Settings/Users/DetailsDrawer/Content.tsx
   */
  useEffect(() => {
    if (!data?.managedUserGroups || searchParams.size === 0) return;

    const identityProviderParam = searchParams.get("identityProvider");
    const groupNameParam = searchParams.get("groupName");

    if (!identityProviderParam || !groupNameParam) return;

    const currentGroup = data.managedUserGroups.find(
      ({ identityProvider, groupName }) =>
        identityProvider === identityProviderParam && groupName === groupNameParam
    );

    if (currentGroup) {
      showIdpGroupMappingDetailsDrawer({ currentGroup, activationStatus });
    }

    // Clean up URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("identityProvider");
    newParams.delete("groupName");

    setSearchParams(newParams, { replace: true });
  }, [activationStatus, data, searchParams, setSearchParams]);

  const ErrorContent = useErrorHandle(error);
  if (ErrorContent) {
    return ErrorContent;
  }

  if (!loading && !data?.managedUserGroups) {
    return <NotFoundPage />;
  }

  const hasGroups = !!data?.managedUserGroups?.length;
  const loadingContent = loading && !hasGroups;

  return (
    <IdpGroupMappingPageLayout>
      {!loadingContent && !hasGroups && <IdpGroupMappingEmpty />}
      {(hasGroups || loadingContent) && (
        <IdpGroupTable groups={data?.managedUserGroups || []} loadingContent={loadingContent} />
      )}
    </IdpGroupMappingPageLayout>
  );
};

export default IdpGroupMapping;
